import { SaveNoSlotParams } from '../interfaces/AvailableNoSlots';
import { BusinessReviews } from '../interfaces/BusinessReviews';
import { ContactQuestionParams } from '../interfaces/Contact';
import { Customer } from '../interfaces/Customer';
import { FAQCategory } from '../interfaces/FAQ';
import {
  MeterkastImageUploadParams,
  MeterkastPresignedRequest,
  MeterkastPresignedUpload,
} from '../interfaces/Meterkast';
import { NoTopSlot } from '../interfaces/NoTopSlot';
import { Signature, SignatureImageUploadParams, SignatureUpdateParams } from '../interfaces/Signature';
import { Technician } from '../interfaces/Technician';
import { RemoteTicket } from '../interfaces/Ticket';
import { GroupedSlot } from '../interfaces/TopSlot';
import { middlelayer } from '../services/AxiosService';
import { BackendRepository } from './interfaces/BackendRepository';

export default class BackendRepositoryRemote implements BackendRepository {
  public bookSlot(ticketHashId: string, serviceAppointmentId: string, startDateTime: string, endDateTime: string): Promise<void> {
    const body = {
      start: startDateTime,
      end: endDateTime
    };

    return middlelayer.post<void, typeof body>(`/v2/${ticketHashId}/appointment/${serviceAppointmentId}/schedule`, body);
  }

  public cancelTicket(serviceAppointmentId: string): Promise<void> {
    return middlelayer.post<void, undefined>(`/appointment/${serviceAppointmentId}/cancel`);
  }

  public completeQuestionnaire(serviceAppointmentId: string): Promise<void> {
    return middlelayer.post<void, undefined>(`/appointment/${serviceAppointmentId}/questionnaire`);
  }

  public async getBusinessReviews(): Promise<BusinessReviews> {
    return middlelayer.get<BusinessReviews>('/reviews');
  }

  public async getFAQ(ticketHashId: string, language: string): Promise<FAQCategory[]> {
    return middlelayer.get<FAQCategory[]>(`/${ticketHashId}/faq/${language}`);
  }

  public async getNoTopSlots(ticketHashId: string, workorderId: string): Promise<NoTopSlot[]> {
    return middlelayer.get<NoTopSlot[]>(`/${ticketHashId}/workorder/${workorderId}/no-topslots`);
  }

  public async getSignature(ticketHashId: string): Promise<Signature> {
    return middlelayer.get<Signature>(`/v2/${ticketHashId}/signature`);
  }

  public async getTechnician(ticketHashId: string): Promise<Technician> {
    return middlelayer.get<Technician>(`/${ticketHashId}/visitcard`);
  }

  public async getTicket(ticketHashId: string): Promise<RemoteTicket> {
    return middlelayer.get<RemoteTicket>(`/${ticketHashId}/ticket`);
  }

  public async getTopSlots(
    ticketHashId: string,
    serviceAppointmentId: string,
    startDateTime: string,
    endDateTime: string,
    abortSignal?: AbortSignal
  ): Promise<GroupedSlot[]> {
    const url: string = `/v2/${ticketHashId}/appointment/${serviceAppointmentId}/topslots?grouped=true`;

    const body: { endDate: string; startDate: string } = {
      startDate: startDateTime,
      endDate: endDateTime
    };

    return middlelayer.post<GroupedSlot[], typeof body>(url, body, { signal: abortSignal });
  }

  public meterkastConfirm(jwt: string): Promise<void> {
    return middlelayer.post<void, void>('/v1/meterkast/confirm', undefined, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
  }

  public meterkastPresign(body: MeterkastPresignedRequest, jwt: string): Promise<MeterkastPresignedUpload[]> {
    return middlelayer.post<MeterkastPresignedUpload[], MeterkastPresignedRequest>('/v1/meterkast/presigned', body, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
  }

  public saveNoSlotInformation(ticketHash: string, workorderId: string, params: SaveNoSlotParams): Promise<void> {
    const { version, ...newParams } = params;

    if (version === 'new') {
      return middlelayer.post<void, typeof params.noSlots>(
        `/${ticketHash}/workorder/${workorderId}/no-topslots`, params.noSlots
      );
    }

    return middlelayer.put<void, typeof newParams>(`/${ticketHash}/workorder/${workorderId}/no-topslots`, newParams);
  }

  public sendContactQuestion(ticketHash: string, params: ContactQuestionParams): Promise<void> {
    return middlelayer.post<void, ContactQuestionParams>(`/${ticketHash}/question`, params);
  }

  public async updateCustomer(ticketHashId: string, customer: Customer): Promise<Customer> {
    return middlelayer.put<Customer, Customer>(`/${ticketHashId}/customer/${customer.id}`, customer);
  }

  public updateSignature({ hashId, ...body }: SignatureUpdateParams): Promise<void> {
    return middlelayer.patch<void, Pick<SignatureUpdateParams, 'completed' | 'customerSigned'>>(
      `/v2/${hashId}/signature`, body
    );
  }

  public uploadImage({ presignedUploadUrl, body }: SignatureImageUploadParams): Promise<void> {
    return middlelayer.post<void, SignatureImageUploadParams['body']>(presignedUploadUrl, body, { baseURL: '' });
  }

  public uploadMeterkastImage({ url, body }: MeterkastImageUploadParams): Promise<void> {
    return middlelayer.post<void, MeterkastImageUploadParams['body']>(url, body, { baseURL: '' });
  }
}
